import React from "react";
import { CheckCircle2, Target, Eye } from "lucide-react";
import Reveal from "./Reveal";

/**
 * About section — used inside AboutPage.
 * "Engineers, Architects And Builders — under one roof"
 */
export default function About() {
  return (
    <section className="relative bg-white py-20 lg:py-28" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          {/* Imagery — real LNZ project: N-S Highway engineers on site */}
          <Reveal className="order-2 lg:order-1">
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden bg-slate-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_lnz-projects/artifacts/7kfpgtvz_highway.png"
                  alt="LNZ engineers on the Jamaica North–South Highway slope inspection"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              {/* Overlayed card */}
              <div className="absolute -right-4 -bottom-8 lg:-right-10 lg:-bottom-10 bg-[#0A192F] text-white p-6 lg:p-8 max-w-[300px] shadow-xl" data-testid="about-accent-card">
                <div className="text-[11px] tracking-[0.3em] font-semibold text-[#F5A400] mb-3">EST. 2019</div>
                <div className="font-display text-lg lg:text-xl font-bold leading-snug">
                  LNZ Designs &amp; Development Co. Ltd delivers high-quality residential, light commercial, and infrastructure solutions across Jamaica and the Caribbean.
                </div>
              </div>
              {/* Blueprint accent */}
              <div className="hidden lg:block absolute -left-8 -top-8 w-40 h-40 border-2 border-[#F5A400] pointer-events-none" />
            </div>
          </Reveal>

          {/* Text */}
          <Reveal className="order-1 lg:order-2">
            <div className="flex items-center gap-3 mb-5" data-testid="about-overline">
              <span className="h-px w-10 bg-[#123E73]" />
              <span className="text-xs tracking-[0.3em] font-semibold text-[#123E73]">WHO WE ARE</span>
            </div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-[#0A192F]">
              Engineers, Architects And Builders — under one roof.
            </h2>
            <p className="mt-6 text-base lg:text-lg text-slate-600 leading-relaxed max-w-xl">
              LNZ Designs &amp; Development Co. Ltd is a Jamaica-based construction and
              development firm engineered for clients who demand excellence. We integrate
              architecture, engineering and construction under a single accountable partner —
              eliminating the handoff gaps that compromise budgets and timelines.
            </p>
            <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-xl">
              From luxury residences and commercial towers to roads and public infrastructure,
              we build what the Caribbean&apos;s next generation of owners, investors and diaspora
              clients demand: reliable delivery, innovative design and unwavering quality.
            </p>

            {/* Mission & Vision */}
            <div className="mt-10 grid sm:grid-cols-2 gap-6" data-testid="mission-vision">
              <div className="border-l-2 border-[#F5A400] pl-5">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-4 h-4 text-[#F5A400]" />
                  <div className="text-xs tracking-[0.25em] font-semibold text-[#123E73]">MISSION</div>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Deliver every project with precision, transparency and long-term value — from concept to completion.
                </p>
              </div>
              <div className="border-l-2 border-[#123E73] pl-5">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-4 h-4 text-[#123E73]" />
                  <div className="text-xs tracking-[0.25em] font-semibold text-[#123E73]">VISION</div>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Define the benchmark for Caribbean engineering, architecture and development on a global stage.
                </p>
              </div>
            </div>

            {/* Checkmark list */}
            <ul className="mt-8 space-y-3">
              {[
                "Fully licensed in Jamaica with international-standard QA",
                "BIM-driven design and real-time project dashboards",
                "End-to-end accountability: land to final handover",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-[#F5A400] shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
